import { normalize } from 'normalizr';
import _find from 'lodash/find';
import _keys from 'lodash/keys';
import { trackOnboardingCompleted, onboardTrackingConstants } from '@zola-helpers/client';

import { fetchWeddingAccount } from 'actions/WeddingActions';
import ApiService from '../../util/api';
import featureFlags from '../../util/featureFlags';

import { submitOnboarding } from '../../actions/OnboardActions';
import { getCardByVariationUUID } from './cardCatalogActions'; // eslint-disable-line

// Selectors
import {
  getProjectUUID,
  getCustomizationTypeUUIDs,
  getCustomizations,
  getStepGroups,
  getSize,
} from '../selectors/cardProjectSelector';

import { customizationObject } from '../schemas/projectSchema';
import { getSelectedGuestIds } from '../selectors/Customization';
import { getMinImageSize, getTypeFromCustomization } from '../util/customization';

import { MAX_CARD_DIMENSION_INCHES, CARD_SIZES } from '../constants/Cards';

export const ActionTypes = {
  RECEIVE_CUSTOMIZATION: 'zola/card/project/RECEIVE_CUSTOMIZATION',
  RECEIVE_ADD_TEXT_ELEMENT: 'zola/card/project/RECEIVE_ADD_TEXT_ELEMENT',
  RECEIVE_RESTORED_ELEMENT: 'zola/card/project/RECEIVE_RESTORED_ELEMENT',
  RECEIVE_DELETE_ELEMENT: 'zola/card/project/RECEIVE_DELETE_ELEMENT',
  RECEIVE_DELETE_ELEMENT_IMAGE: 'zola/card/project/RECEIVE_DELETE_ELEMENT_IMAGE',
  RECEIVE_PROJECT_AUTHENTICATION: 'zola/card/project/RECEIVE_PROJECT_AUTHENTICATION',
  RECEIVE_PROJECT_GUEST_IDS: 'zola/card/project/guests/RECEIVE_PROJECT_GUEST_IDS',
  RECEIVE_PROJECT_GUESTS: 'zola/card/project/guests/RECEIVE_PROJECT_GUESTS',
  RECEIVE_PROJECT: 'zola/cards/project/RECEIVE_PROJECT',
  REQUEST_CUSTOMIZATION: 'zola/cards/project/REQUEST_CUSTOMIZATION',
  REQUEST_UPDATE_CUSTOMIZATION: 'zola/cards/project/REQUEST_UPDATE_CUSTOMIZATION',
  REQUEST_PROJECT_AUTHENTICATION: 'zola/cards/project/REQUEST_PROJECT_AUTHENTICATION',
  REQUEST_PROJECT_GUEST_IDS: 'zola/cards/project/guests/REQUEST_PROJECT_GUEST_IDS',
  REQUEST_PROJECT: 'zola/cards/project/REQUEST_PROJECT',
  SET_ACTIVE_FIELD: 'zola/cards/project/SET_ACTIVE_FIELD',
  GET_CUSTOMIZATION_FONTS_REQUEST: 'zola/cards/project/GET_CUSTOMIZATION_FONTS_REQUEST',
  GET_CUSTOMIZATION_FONTS_SUCCESS: 'zola/cards/project/GET_CUSTOMIZATION_FONTS_SUCCESS',
  GET_CUSTOMIZATION_FONTS_FAILURE: 'zola/cards/project/GET_CUSTOMIZATION_FONTS_FAILURE',
  REQUEST_ADD_PROJECT_TO_CART: 'zola/cards/project/REQUEST_ADD_PROJECT_TO_CART',
  RECEIVE_ADD_PROJECT_TO_CART: 'zola/cards/project/RECEIVE_ADD_PROJECT_TO_CART',
  RECEIVE_COMPLETE_DIGITAL_PROJECT: 'zola/cards/project/RECEIVE_COMPLETE_DIGITAL_PROJECT',
  RECEIVE_DIGITAL_PROJECT_SUCCESS: 'zola/cards/project/RECEIVE_DIGITAL_PROJECT_SUCCESS',
  UPDATE_DIGITAL_ORDER_REQUESTED: 'zola/cards/project/UPDATE_DIGITAL_ORDER_REQUESTED',
  REQUEST_DIGITAL_ORDER: 'zola/cards/project/REQUEST_DIGITAL_ORDER',
  REQUEST_DIGITAL_PROOF: 'zola/cards/project/REQUEST_DIGITAL_PROOF',
  CREATE_DIGITAL_PROOF_REQUEST: 'zola/cards/project/CREATE_DIGITAL_PROOF_REQUEST',
  UPDATE_DIGITAL_PROOF_REQUEST: 'zola/cards/project/UPDATE_DIGITAL_PROOF_REQUEST',
  REQUEST_PROJECT_META: 'zola/cards/project/REQUEST_PROJECT_META',
  RECEIVE_PROJECT_META: 'zola/cards/project/RECEIVE_PROJECT_META',
  COMPLETE_FONT_RESIZE: 'zola/cards/project/COMPLETE_FONT_RESIZE',
  OVERRIDE_FONT_SIZES: 'zola/cards/project/OVERRIDE_FONT_SIZES',
  ADD_TO_FOIL_HISTORY: 'zola/cards/project/ADD_TO_FOIL_HISTORY',
  REMOVE_ONE_FROM_FOIL_HISTORY: 'zola/cards/project/REMOVE_ONE_FROM_FOIL_HISTORY',
  ADD_TO_DELETE_HISTORY: 'zola/cards/project/ADD_TO_DELETE_HISTORY',
  REMOVE_ONE_FROM_DELETE_FOIL_HISTORY: 'zola/cards/project/REMOVE_ONE_FROM_DELETE_FOIL_HISTORY',
  SET_LANDING_PAGE_VISIBILITY: 'zola/cards/project/SET_LANDING_PAGE_VISIBILITY',
  UPDATE_IMAGE_SIZE_REQUIREMENTS: 'zola/cards/project/UPDATE_IMAGE_SIZE_REQUIREMENTS',
};

const findNextVariation = (getState, customizationUUID, nextOptionValues) => {
  const state = getState();
  const customizations = getCustomizations(state);

  // get customization using customziationUUID argument
  const customization = customizations[customizationUUID];
  // grab variation from customization - this will only work for new sku model
  // variation.option_values needed to merge nextOptionValues with
  const {
    variation: { option_values: currentOptionValues },
  } = customization;
  // mergedOptionValues is what we use to find a matching variation from the associated
  // card
  const mergedOptionValues = {
    ...currentOptionValues,
    ...nextOptionValues,
  };

  // * If applicable, Ignore envelope-type in the finding process for now (envelope color and envelope-type are 1:1)
  delete mergedOptionValues['envelope-type'];

  // find card associated with current customization, each card has a customizationUUID for this purpose
  const customizationCard = _find(getState().cards.cardCatalog.cards, {
    customizationUUID,
  });
  // grab next variation - this might need some error handling in the case that a variation is not found, however
  // that should be rare
  const nextVariation = _find(customizationCard.variations, {
    option_values: mergedOptionValues,
  });

  return nextVariation;
};

function requestProjectMeta() {
  return {
    type: ActionTypes.REQUEST_PROJECT_META,
  };
}

function receiveProjectMeta(projectMeta) {
  return {
    type: ActionTypes.RECEIVE_PROJECT_META,
    payload: {
      projectMeta,
    },
  };
}

function requestProject() {
  return {
    type: ActionTypes.REQUEST_PROJECT,
  };
}

export function receiveProject(project, isTestProject) {
  return {
    type: ActionTypes.RECEIVE_PROJECT,
    payload: {
      project,
      isTestProject,
    },
  };
}

function requestCustomization() {
  return {
    type: ActionTypes.REQUEST_CUSTOMIZATION,
  };
}

function requestUpdateCustomization(customizationUUID) {
  return {
    type: ActionTypes.REQUEST_UPDATE_CUSTOMIZATION,
    payload: { customizationUUID },
  };
}

function getCustomizationFontsRequest() {
  return {
    type: ActionTypes.GET_CUSTOMIZATION_FONTS_REQUEST,
  };
}

function getCustomizationFontsSuccess(fonts) {
  return {
    type: ActionTypes.GET_CUSTOMIZATION_FONTS_SUCCESS,
    payload: {
      fonts,
    },
  };
}

function getCustomizationFontsFailure(error) {
  return {
    type: ActionTypes.GET_CUSTOMIZATION_FONTS_FAILURE,
    payload: {
      error,
    },
  };
}

function receiveCustomization(customization) {
  return {
    type: ActionTypes.RECEIVE_CUSTOMIZATION,
    payload: {
      customization,
    },
  };
}

function receiveAddTextElement(customizationUUID, pageUUID, element) {
  return {
    type: ActionTypes.RECEIVE_ADD_TEXT_ELEMENT,
    payload: {
      customizationUUID,
      pageUUID,
      element,
    },
  };
}

function receiveRestoredElement(customizationUUID, pageUUID, elements, lastChanges) {
  return {
    type: ActionTypes.RECEIVE_RESTORED_ELEMENT,
    payload: {
      customizationUUID,
      pageUUID,
      elements,
      lastChanges,
    },
  };
}

function receiveDeleteElement(pageUUID, elementUUIDs) {
  return {
    type: ActionTypes.RECEIVE_DELETE_ELEMENT,
    payload: {
      pageUUID,
      elementUUIDs,
    },
  };
}

function receiveDeleteElementImage(elementUUID, element) {
  return {
    type: ActionTypes.RECEIVE_DELETE_ELEMENT_IMAGE,
    payload: {
      elementUUID,
      element,
    },
  };
}

function requestProjectGuestIds() {
  return {
    type: ActionTypes.REQUEST_PROJECT_GUEST_IDS,
  };
}

function receiveProjectGuestIds(projectGuestIds) {
  return {
    type: ActionTypes.RECEIVE_PROJECT_GUEST_IDS,
    payload: {
      projectGuestIds,
    },
  };
}

function receiveProjectGuests(projectGuests) {
  return {
    type: ActionTypes.RECEIVE_PROJECT_GUESTS,
    payload: { projectGuests },
  };
}

function overrideFontSizes(fontSizeOverrides) {
  return {
    type: ActionTypes.OVERRIDE_FONT_SIZES,
    payload: { fontSizeOverrides },
  };
}

function completeFontResize() {
  return {
    type: ActionTypes.COMPLETE_FONT_RESIZE,
  };
}

function requestProjectAuthentication() {
  return {
    type: ActionTypes.REQUEST_PROJECT_AUTHENTICATION,
  };
}

function requestAddProjectToCart() {
  return {
    type: ActionTypes.REQUEST_ADD_PROJECT_TO_CART,
  };
}

function receiveAddProjectToCart() {
  return {
    type: ActionTypes.RECEIVE_ADD_PROJECT_TO_CART,
  };
}

function receiveCompleteDigitalProject(json) {
  return {
    type: ActionTypes.RECEIVE_COMPLETE_DIGITAL_PROJECT,
    payload: json,
  };
}

function receiveDigitalProjectSuccess() {
  return {
    type: ActionTypes.RECEIVE_DIGITAL_PROJECT_SUCCESS,
  };
}

export function requestDigitalOrder() {
  return {
    type: ActionTypes.REQUEST_DIGITAL_ORDER,
  };
}

export function createDigitalProofRequest() {
  return {
    type: ActionTypes.CREATE_DIGITAL_PROOF_REQUEST,
  };
}

export function updateDigitalProofRequest() {
  return {
    type: ActionTypes.UPDATE_DIGITAL_PROOF_REQUEST,
  };
}

export function setActiveField(activeField) {
  return {
    type: ActionTypes.SET_ACTIVE_FIELD,
    payload: {
      activeField,
    },
  };
}

// Customization TYC Generator Inside #INVITES-1094
export function setLandingPageVisibility(status) {
  return {
    type: ActionTypes.SET_LANDING_PAGE_VISIBILITY,
    payload: {
      status,
    },
  };
}

export function fetchProjectMeta(projectUUID) {
  return dispatch => {
    dispatch(requestProjectMeta());
    return ApiService.get(`/web-api/v2/card-project/${projectUUID}/meta`).then(json => {
      dispatch(receiveProjectMeta(json));
    });
  };
}

export function createProject(suiteUUID, activeVariationUUID, quantity, isAdminView = false) {
  return dispatch => {
    // eslint-disable-line
    const requestBody = {
      suite_uuid: suiteUUID,
      lead_variation_uuid: activeVariationUUID,
      quantity,
      from_admin_route: isAdminView,
    };
    return ApiService.post('/web-api/v3/card-project', requestBody).then(json => {
      dispatch(setLandingPageVisibility(true));
      dispatch(fetchProjectMeta(json.uuid));
      json.customizations.forEach(customization => {
        if (customization.variation && customization.variation.uuid) {
          dispatch(
            getCardByVariationUUID(customization.variation.uuid, customization.uuid, isAdminView)
          );
        }
      });
      dispatch(receiveProject(json, isAdminView));
      return json;
    });
  };
}

export function fetchProject(projectUUID) {
  return dispatch => {
    // eslint-disable-line
    dispatch(setLandingPageVisibility(false));
    dispatch(requestProject());
    return ApiService.get(`/web-api/v2/card-project/${projectUUID}`).then(json => {
      const isTestProject = !!json.test_project;
      dispatch(fetchProjectMeta(projectUUID));
      json.customizations.forEach(customization => {
        if (customization.variation && customization.variation.uuid) {
          dispatch(
            getCardByVariationUUID(customization.variation.uuid, customization.uuid, isTestProject)
          );
        }
      });
      dispatch(receiveProject(json, isTestProject));
      return json;
    });
  };
}

// TODO: can we remove this? Is it still used anywhere?
export function fetchPublicProject(projectUUID) {
  return dispatch =>
    ApiService.get(`/web-api/v2/card-project/digital/${projectUUID}/public`).then(json => {
      json.customizations.forEach(customization => {
        if (customization.variation && customization.variation.uuid) {
          dispatch(getCardByVariationUUID(customization.variation.uuid, customization.uuid));
        }
      });
      dispatch(receiveProject(json));
      return json;
    });
}

// gets called for outside (bigger? outter?) updates like changing the quantity of cards
export function updateProject(projectUUID, customizations, isProof = false) {
  return dispatch => {
    return ApiService.put(`/web-api/v2/card-project/${projectUUID}`, {
      proof: isProof,
      customizations,
    }).then(json => {
      const isTestProject = !!json.test_project;
      json.customizations.forEach(customization => {
        if (customization.variation && customization.variation.uuid) {
          dispatch(
            getCardByVariationUUID(customization.variation.uuid, customization.uuid, isTestProject)
          );
        }
      });
      dispatch(receiveProject(json, isTestProject));
      return json;
    });
  };
}

export function cloneProject(projectUUID) {
  return dispatch =>
    ApiService.post(`/web-api/v2/card-project/${projectUUID}/clone`).then(json => {
      const { uuid } = json;
      return dispatch(fetchProject(uuid));
    });
}

export function cloneProjectToMedium(projectUUID, medium) {
  return dispatch => {
    const requestBody = {
      card_project_uuid: projectUUID,
      medium,
    };
    return ApiService.post(
      `/web-api/v2/card-project/${projectUUID}/clone/medium`,
      requestBody
    ).then(json => {
      const { uuid } = json;
      return dispatch(fetchProject(uuid));
    });
  };
}

export function cloneSampleProject(projectUUID, variationUUID, quantity) {
  return dispatch => {
    const requestBody = {
      single_sample_project_uuid: projectUUID,
      variation_uuid: variationUUID,
      quantity,
    };
    return ApiService.post(
      `/web-api/v1/card-single-sample/create-project-from-sample`,
      requestBody
    ).then(json => dispatch(fetchProject(json.uuid)));
  };
}

export function fetchCustomization(uuid, isAdminView = false) {
  return dispatch => {
    dispatch(requestCustomization());
    return ApiService.get(`/web-api/v2/card-project/customization/${uuid}`).then(json => {
      if (json.variation && json.variation.uuid) {
        dispatch(getCardByVariationUUID(json.variation.uuid, json.uuid, isAdminView));
      }
      const normalizedCustomization = normalize(json, customizationObject);
      // console.log('normalizedCustomization: ', normalizedCustomization);
      dispatch(receiveCustomization(normalizedCustomization));
      return json;
    });
  };
}

export function updateCustomization(customizationUUID, customizationData, isTestProject = false) {
  return dispatch => {
    // eslint-disable-line
    // TODO: need to add this back in to set flag that an update request is in progress
    dispatch(requestUpdateCustomization(customizationUUID)); // - legacy, not related to cookie or test
    return ApiService.put(
      `/web-api/v2/card-project/customization/${customizationUUID}`,
      customizationData
    ).then(json => {
      if (json.variation && json.variation.uuid) {
        dispatch(getCardByVariationUUID(json.variation.uuid, json.uuid, isTestProject));
      }
      const normalizedCustomization = normalize(json, customizationObject);
      // console.log('normalizedCustomization: ', normalizedCustomization);
      dispatch(receiveCustomization(normalizedCustomization));
      return json;
    });
  };
}

export function addTextElement(customizationUUID, pageUUID, customizationData) {
  return dispatch =>
    ApiService.post(
      `/web-api/v2/card-project/customization/page/${pageUUID}/element`,
      customizationData
    ).then(json => {
      dispatch(receiveAddTextElement(customizationUUID, pageUUID, json));
      return json;
    });
}

export function restoreElement(customizationUUID, pageUUID, elementUUID, lastChanges) {
  return dispatch =>
    ApiService.post(`/web-api/v2/card-project/element/${elementUUID}/restore`).then(json => {
      dispatch(receiveRestoredElement(customizationUUID, pageUUID, json, lastChanges));
      return json;
    });
}

// TODO: remove customizationUUID from parameters when the signature no longer
// needs to match signature of legacy cardProjectActions.deleteElement
export function deleteElement(pageUUID, elementUUIDs) {
  return dispatch =>
    ApiService.delete(
      `/web-api/v2/card-project/element/${elementUUIDs[0]}`,
      {},
      {},
      {},
      'text'
    ).then(json => {
      dispatch(receiveDeleteElement(pageUUID, elementUUIDs));
      return json;
    });
}

export function deleteElementImage(elementUUID) {
  return dispatch =>
    ApiService.delete(`/web-api/v2/card-project/element/${elementUUID}/image`).then(json => {
      dispatch(receiveDeleteElementImage(elementUUID, json));
      return json;
    });
}

export function toggleRecipientAddressing(
  customizationUUID,
  enableRecipientAddressing,
  isExpeditorFilled
) {
  return (dispatch, getState) => {
    let requestBody = {
      recipient_addressing: enableRecipientAddressing,
    };
    const projectSize = getSize(getState());
    if (
      featureFlags.get('envelopeVariationDefault') &&
      !isExpeditorFilled &&
      projectSize !== CARD_SIZES.postcard
    ) {
      const nextVariation = findNextVariation(getState, customizationUUID, {
        'envelope-printing': enableRecipientAddressing ? 'front' : 'none',
      });
      requestBody = {
        ...requestBody,
        variation_uuid: nextVariation.uuid,
      };
    }
    // actually just hits the same endpoint as updateCustomization, just with a request
    // body specific to recipient addressing
    return ApiService.put(
      `/web-api/v2/card-project/customization/${customizationUUID}`,
      requestBody
    )
      .then(json => {
        const normalizedCustomization = normalize(json, customizationObject);
        // console.log('normalizedCustomization: ', normalizedCustomization);
        dispatch(receiveCustomization(normalizedCustomization));
        return json;
      })
      .then(json => {
        const { userContext } = getState().user;
        // if the user does not have a wedding account we need to create one so that they can
        // add guests to guest list. This logic used to be handled in components but probably fits
        // better here because a wedding account must be created if recipient addressing is toggled on
        // for a user without a wedding account
        if (enableRecipientAddressing && !userContext.has_wedding_account) {
          const onboardingData = {
            existing_account: true,
            business_unit: 'WEDDING_GUEST_LIST',
            answers: [],
          };
          return dispatch(submitOnboarding(onboardingData)).then(() => {
            trackOnboardingCompleted(
              onboardTrackingConstants.BUSINESS_UNIT.WEDDINGS,
              onboardTrackingConstants.COMPONENT.WEDDINGS,
              { isExistingUser: true }
            );
            // * Fetch and store in state the freshly created data regarding wedding account
            dispatch(fetchWeddingAccount());
            return json;
          });
        }
        return json;
      });
  };
}

export function getMediaFileUrl(mediaFileUUID) {
  return () =>
    ApiService.get(
      `/web-api/v1/card-project/media_file/${mediaFileUUID}/presigned_url`,
      {},
      {},
      'text'
    );
}

export function getProjectGuestIds(projectUUID) {
  return dispatch => {
    dispatch(requestProjectGuestIds());

    if (featureFlags.get('invitesGuestFontOverrides')) {
      return ApiService.get(`/web-api/v2/card-project/${projectUUID}/guests`).then(json =>
        dispatch(receiveProjectGuests(json))
      );
    }
    return ApiService.get(`/web-api/v1/card-project/${projectUUID}/guests`).then(json =>
      dispatch(receiveProjectGuestIds(json))
    );
  };
}

export function updateProjectFontSizeOverrides(fontSizeOverrides = {}) {
  return (dispatch, getState) => {
    dispatch(overrideFontSizes(fontSizeOverrides)); // Save overrides immediately
    const projectUUID = getProjectUUID(getState());
    const requestBody = Object.keys(fontSizeOverrides).map(guestId => {
      const overrides = fontSizeOverrides[guestId];
      return {
        guest_group_id: guestId,
        enabled: true,
        guest_name_font_size_override: overrides.name || 0,
        address_font_size_override: overrides.address || 0,
      };
    });
    return ApiService.put(
      `/web-api/v2/card-project/${projectUUID}/guests`,
      requestBody
    ).then(json => dispatch(receiveProjectGuests(json))); // Set overrides again with API response
  };
}

export function updateProjectGuestIds(projectGuestIds) {
  return (dispatch, getState) => {
    const projectUUID = getProjectUUID(getState());
    const requestBody = {
      recipients: projectGuestIds,
    };
    return ApiService.put(
      `/web-api/v1/card-project/${projectUUID}/guests`,
      requestBody
    ).then(json => dispatch(receiveProjectGuestIds(json)));
  };
}

export function updateProjectGuestId(guestId) {
  return (dispatch, getState) => {
    const state = getState();
    const selectedGuestIds = getSelectedGuestIds(state);
    const projectUUID = getProjectUUID(state);
    const isCurrentlySelected = selectedGuestIds.indexOf(guestId) > -1;
    const requestBody = {
      recipients: {
        [guestId]: !isCurrentlySelected,
      },
    };
    return ApiService.put(
      `/web-api/v1/card-project/${projectUUID}/guests`,
      requestBody
    ).then(json => dispatch(receiveProjectGuestIds(json)));
  };
}

export function getCustomizationFonts(customizationUUID) {
  return dispatch => {
    dispatch(getCustomizationFontsRequest());
    return (
      ApiService.get(`/web-api/v2/card-project/customization/${customizationUUID}/fonts`)
        .then(data => dispatch(getCustomizationFontsSuccess(data)))
        // TODO: This can be move to handleErrors and pass as param the action to fire on failure
        // But we'll keep it specific to this method for now until we handle errors gobally
        .catch(err => dispatch(getCustomizationFontsFailure(err)))
    );
  };
}

// NEW ACTIONS FOR SKU MIGRATION

export function getProjectAuthenticated(projectUUID) {
  return dispatch => {
    dispatch(requestProjectAuthentication());
    return ApiService.post(`/web-api/v3/card-project/${projectUUID}/is_authenticated`).then(
      auth => auth
    );
  };
}

// generic function to update variation_uuid of a customization
export function updateCustomizationVariation(
  customizationUUID,
  nextOptionValues,
  isTestProject = false
) {
  return (dispatch, getState) => {
    const nextVariation = findNextVariation(getState, customizationUUID, nextOptionValues);
    const requestBody = {
      variation_uuid: nextVariation.uuid,
    };
    return ApiService.put(
      `/web-api/v2/card-project/customization/${customizationUUID}`,
      requestBody
    ).then(json => {
      if (json.variation && json.variation.uuid) {
        dispatch(getCardByVariationUUID(json.variation.uuid, json.uuid, isTestProject));
      }
      const normalizedCustomization = normalize(json, customizationObject);
      dispatch(receiveCustomization(normalizedCustomization));
    });
  };
}

// moving away from 'soft deleting' customizations (like for RSVP), so instead we have a
// field on the customization object 'inactive' that we can use for the same purposes
export function toggleCustomizationActive(customizationUUID, bool) {
  return (dispatch, getState) => {
    const requestBody = {
      inactive: bool,
    };
    // actually just hits the same endpoint as updateCustomization, just with a request
    // body specifically for updating inactive property
    return ApiService.put(
      `/web-api/v2/card-project/customization/${customizationUUID}`,
      requestBody
    ).then(json => {
      const normalizedCustomization = normalize(json, customizationObject);
      dispatch(receiveCustomization(normalizedCustomization));
      if (json.type === 'RSVP') {
        const rsvpEnvelopeUUID = getState().cards.project.customizationTypeToUUID.RSVP_ENVELOPE;
        if (rsvpEnvelopeUUID) {
          // IMPORTANT: this could be a bad idea recursively calling and update request
          dispatch(toggleCustomizationActive(rsvpEnvelopeUUID, bool));
        }
      }
    });
  };
}

// function that handles using v1 endpoint for projects with old sku model or v3 endpoint
// for projects with new sku model - with this action conditional logic doesnt have to be
// implemented in components
export function addProjectToCart() {
  return (dispatch, getState) => {
    const state = getState();
    const projectUUID = getProjectUUID(state);
    const requestBody = {
      projectUUID,
    };
    dispatch(requestAddProjectToCart());
    return ApiService.post('/web-api/v3/card-project/cart', requestBody, {}, {}, 'text')
      .then(() => dispatch(receiveAddProjectToCart()))
      .then(() => {
        window.location = '/cart/checkout';
      });
  };
}

export function updateAllProjectQuantities(quantity, isProof = false) {
  return (dispatch, getState) => {
    const state = getState();
    const projectUUID = getProjectUUID(state);
    const customizations = getCustomizations(state);
    const formattedCustomizations = _keys(customizations).reduce((result, uuid) => {
        result[uuid] = { quantity }; // eslint-disable-line
      return result;
    }, {});
    return dispatch(updateProject(projectUUID, formattedCustomizations, isProof));
  };
}

export function updateStepGroupQuantities(cardType, quantity) {
  return (dispatch, getState) => {
    const state = getState();
    const projectUUID = getProjectUUID(state);

    const stepGroups = getStepGroups(state);
    const currentGroup = stepGroups.find(group => group.cardType === cardType);
    const groupCardTypes = currentGroup.steps.map(s => s.cardType);

    const customizations = getCustomizations(state);
    const customizationsToUpdate = Object.values(customizations).filter(c =>
      groupCardTypes.includes(getTypeFromCustomization(c))
    );

    const formattedCustomizations = customizationsToUpdate.reduce(
      (result, { uuid }) => ({
        ...result,
        [uuid]: { quantity },
      }),
      {}
    );
    return dispatch(updateProject(projectUUID, formattedCustomizations));
  };
}

// uses updateProject action to update inactive field of both rsvp and rsvp envelope cards
export function updateRSVPInactive(inactive) {
  return (dispatch, getState) => {
    // const { projectUUID } = getState().cards.cardProject;
    const state = getState();
    const projectUUID = getProjectUUID(state);
    const customizationTypeToUUID = getCustomizationTypeUUIDs(state);
    // const { customizationTypeToUUID } = getState().cards.cardProject;
    const rsvpUUID = customizationTypeToUUID.RSVP;
    const rsvpEnvelopeUUID = customizationTypeToUUID.RSVP_ENVELOPE;
    const customizations = {
      [rsvpUUID]: {
        inactive,
      },
      [rsvpEnvelopeUUID]: {
        inactive,
      },
    };
    return dispatch(updateProject(projectUUID, customizations));
  };
}

// Uses updateProject action to update a customization's `inactive` property
export function updateCustomizationInactive(inactive, customizationUUID, projectUUID) {
  return dispatch => {
    const updatedCustomizations = { [customizationUUID]: { inactive } };
    return dispatch(updateProject(projectUUID, updatedCustomizations));
  };
}

export function requestDigitalProjectStatus() {
  return (dispatch, getState) => {
    const state = getState();
    const projectUUID = getProjectUUID(state);
    const { digitalStatus } = state.cards.project;
    return ApiService.get(
      `/web-api/v2/card-project/digital/${projectUUID}/status/${digitalStatus.uuid}`
    ).then(json => {
      dispatch(receiveCompleteDigitalProject(json));
    });
  };
}

export function requestCompleteDigitalProject() {
  return (dispatch, getState) => {
    const state = getState();
    const projectUUID = getProjectUUID(state);
    return ApiService.put(`/web-api/v2/card-project/digital/${projectUUID}/complete`)
      .then(json => {
        const { async_request_uuid: asyncRequestUUID } = json;

        dispatch(receiveCompleteDigitalProject(json));
        const interval = setInterval(() => {
          const { uuid, status } = getState().cards.project.digitalStatus;
          if (uuid === asyncRequestUUID && status === 'PROCESSED_SUCCESS') {
            clearInterval(interval);
            dispatch(receiveDigitalProjectSuccess());
          } else if (uuid === asyncRequestUUID && status === 'PROCESSED_FAILURE') {
            clearInterval(interval);
          } else {
            dispatch(requestDigitalProjectStatus());
          }
        }, 1000);
        return json;
      })
      .catch(() => {});
  };
}

export function sendDigitalProofRequest(projectUUID) {
  return (dispatch, getState) => {
    const { userContext } = getState().user;
    const requestBody = {
      card_project_uuid: projectUUID,
      user_object_id: userContext.object_id,
    };
    return ApiService.post('/web-api/v2/card-project/projectUUID/digitalProof', requestBody).then(
      json => json
    );
  };
}

export function requestPublicCard(uuid) {
  return dispatch =>
    ApiService.get(`/web-api/v2/card-project/digital/${uuid}/public`).then(json => {
      dispatch(receiveProject(json.project));
      return json;
    });
}

export function updateProjectMeta() {
  return (dispatch, getState) => {
    const state = getState();
    const projectUUID = getProjectUUID(state);
    return ApiService.put(`/web-api/v2/card-project/${projectUUID}/meta`)
      .then(json => {
        dispatch(receiveProjectMeta(json));
        return json;
      })
      .catch(err => {
        console.log('err: ', err);
      });
  };
}

function addToFoilHistory(newFoil, customizationCardType) {
  return {
    type: ActionTypes.ADD_TO_FOIL_HISTORY,
    payload: {
      newFoil,
      customizationCardType,
    },
  };
}

function removeOneFoilHistory(customizationCardType) {
  return {
    type: ActionTypes.REMOVE_ONE_FROM_FOIL_HISTORY,
    payload: {
      customizationCardType,
    },
  };
}

function addToDeleteFoilHistory(newFoil, customizationCardType) {
  return {
    type: ActionTypes.ADD_TO_DELETE_HISTORY,
    payload: {
      newFoil,
      customizationCardType,
    },
  };
}

function removeOneDeleteFoilHistory(customizationCardType) {
  return {
    type: ActionTypes.REMOVE_ONE_FROM_DELETE_FOIL_HISTORY,
    payload: {
      customizationCardType,
    },
  };
}

function updateImageSizeRequirements(customizableImageElements, page, customizationSize) {
  const maxCardDimensionInches = MAX_CARD_DIMENSION_INCHES[customizationSize];
  const allMinWidths = [];
  const allMinHeights = [];
  const minCrops = {};

  customizableImageElements.forEach(element => {
    const minImageSize = getMinImageSize(element, page, maxCardDimensionInches);

    allMinWidths.push(minImageSize.width);
    allMinHeights.push(minImageSize.height);
    minCrops[element.uuid] = minImageSize;
  });

  const imageSizeRequirements = {
    minWidth: allMinWidths.length ? Math.max(...allMinWidths) : 0,
    minHeight: allMinHeights.length ? Math.max(...allMinHeights) : 0,
    minCrops,
  };

  return {
    type: ActionTypes.UPDATE_IMAGE_SIZE_REQUIREMENTS,
    payload: {
      imageSizeRequirements,
    },
  };
}

export const actions = {
  setActiveField,
  createProject,
  fetchProject,
  updateProject,
  cloneProject,
  cloneProjectToMedium,
  cloneSampleProject,
  fetchCustomization,
  updateCustomization,
  addTextElement,
  restoreElement,
  deleteElement,
  deleteElementImage,
  toggleRecipientAddressing,
  getMediaFileUrl,
  getProjectGuestIds,
  updateProjectFontSizeOverrides,
  completeFontResize,
  updateProjectGuestIds,
  getCustomizationFonts,
  getProjectAuthenticated,
  updateCustomizationVariation,
  toggleCustomizationActive,
  addProjectToCart,
  updateAllProjectQuantities,
  updateStepGroupQuantities,
  updateRSVPInactive,
  updateCustomizationInactive,
  updateProjectGuestId,
  requestDigitalProjectStatus,
  requestCompleteDigitalProject,
  requestDigitalOrder,
  requestPublicCard,
  sendDigitalProofRequest,
  createDigitalProofRequest,
  updateDigitalProofRequest,
  setLandingPageVisibility,
  updateProjectMeta,
  fetchProjectMeta,
  addToFoilHistory,
  removeOneFoilHistory,
  addToDeleteFoilHistory,
  removeOneDeleteFoilHistory,
  updateImageSizeRequirements,
};



// WEBPACK FOOTER //
// ./src/cards/actions/projectActions.js